import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Button,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";

// Icons
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import MedicalInformationOutlinedIcon from "@mui/icons-material/MedicalInformationOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import DomainAddOutlinedIcon from "@mui/icons-material/DomainAddOutlined";
import HealingOutlinedIcon from "@mui/icons-material/HealingOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";

const Settings = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  const [activeItem, setActiveItem] = useState("Practice Setting");

  const menuItems = [
    { label: "Practice Setting", icon: <BusinessRoundedIcon sx={{ fontSize: "1.2rem" }} /> },
    { label: "Insurance", icon: <SecurityRoundedIcon sx={{ fontSize: "1.2rem" }} /> },
    { label: "User", icon: <PersonOutlineRoundedIcon sx={{ fontSize: "1.2rem" }} /> },
    { label: "Diagnosis", icon: <MedicalInformationOutlinedIcon sx={{ fontSize: "1.2rem" }} />, hasSub: true },
    { label: "Medicines", icon: <MedicationOutlinedIcon sx={{ fontSize: "1.2rem" }} /> },
    { label: "Provider", icon: <LocalHospitalOutlinedIcon sx={{ fontSize: "1.2rem" }} /> },
    { label: "Procedure", icon: <AssignmentTurnedInOutlinedIcon sx={{ fontSize: "1.2rem" }} /> },
    { label: "Facilities", icon: <DomainAddOutlinedIcon sx={{ fontSize: "1.2rem" }} /> },
    { label: "Practices", icon: <HealingOutlinedIcon sx={{ fontSize: "1.2rem" }} /> },
    { label: "Modules", icon: <ViewModuleOutlinedIcon sx={{ fontSize: "1.2rem" }} /> },
    { label: "Course", icon: <SchoolOutlinedIcon sx={{ fontSize: "1.2rem" }} /> },
  ];

  // Theme Colors
  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#E2E8F0";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0F172A";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const activeBg = isDark ? "rgba(99, 102, 241, 0.16)" : "#EEF2FF";
  const activeColor = isDark ? "#A5B4FC" : "#4F46E5";
  const inputBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#F8FAFC";

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* Top Header Card */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "24px",
          border: `1px solid ${cardBorder}`,
          p: { xs: 2.2, md: "22px 28px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: isDark
            ? "0 10px 32px rgba(0, 0, 0, 0.5)"
            : "0 4px 24px rgba(15, 23, 42, 0.04)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "16px",
              background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 20px rgba(99, 102, 241, 0.35)",
              flexShrink: 0,
            }}
          >
            <SettingsRoundedIcon sx={{ fontSize: "1.7rem" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: "1.25rem", md: "1.45rem" },
                fontWeight: 800,
                color: primaryTextColor,
                letterSpacing: "-0.4px",
                lineHeight: 1.2,
              }}
            >
              System Settings & Master Data
            </Typography>
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: secondaryTextColor,
                fontWeight: 500,
                mt: 0.3,
              }}
            >
              Configure clinic practices, diagnostic ICD registries, payer policies, and staff roles.
            </Typography>
          </Box>
        </Box>

        <Chip
          label="Super Admin Mode"
          size="small"
          sx={{
            display: { xs: "none", sm: "inline-flex" },
            height: "26px",
            fontWeight: 700,
            fontSize: "0.74rem",
            backgroundColor: isDark ? "rgba(16, 185, 129, 0.15)" : "#ECFDF5",
            color: "#10B981",
            borderRadius: "8px",
          }}
        />
      </Box>

      {/* 2-Column Split: Left List + Right Panel */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "300px 1fr" },
          gap: 3,
          alignItems: "start",
        }}
      >
        {/* Left Side: Category Menu (Matching Screenshot) */}
        <Box
          sx={{
            backgroundColor: cardBg,
            borderRadius: "20px",
            border: `1px solid ${cardBorder}`,
            p: 1.5,
            boxShadow: isDark
              ? "0 10px 30px rgba(0, 0, 0, 0.45)"
              : "0 4px 20px rgba(15, 23, 42, 0.03)",
          }}
        >
          <Typography
            sx={{
              px: 2,
              py: 1.2,
              fontSize: "0.74rem",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: secondaryTextColor,
            }}
          >
            Configuration Modules
          </Typography>

          <List sx={{ p: 0 }}>
            {menuItems.map((item) => {
              const isSelected = activeItem === item.label;
              return (
                <ListItemButton
                  key={item.label}
                  onClick={() => setActiveItem(item.label)}
                  sx={{
                    borderRadius: "12px",
                    mb: 0.5,
                    px: 1.8,
                    py: 1.1,
                    backgroundColor: isSelected ? activeBg : "transparent",
                    color: isSelected ? activeColor : primaryTextColor,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? activeBg
                        : isDark ? "rgba(255, 255, 255, 0.05)" : "#F8FAFC",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: "34px",
                      color: isSelected ? activeColor : secondaryTextColor,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: "0.85rem",
                      fontWeight: isSelected ? 700 : 500,
                    }}
                  />
                  {item.hasSub && (
                    <ChevronRightRoundedIcon sx={{ fontSize: "1.1rem", color: secondaryTextColor }} />
                  )}
                </ListItemButton>
              );
            })}
          </List>
        </Box>

        {/* Right Side: Active Setting Panel */}
        <Box
          sx={{
            backgroundColor: cardBg,
            borderRadius: "20px",
            border: `1px solid ${cardBorder}`,
            p: { xs: 2.5, md: "28px 32px" },
            boxShadow: isDark
              ? "0 10px 30px rgba(0, 0, 0, 0.45)"
              : "0 4px 20px rgba(15, 23, 42, 0.03)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
              pb: 2.5,
              borderBottom: `1px solid ${cardBorder}`,
              mb: 3,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: "1.2rem", fontWeight: 800, color: primaryTextColor }}>
                {activeItem} Configuration
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", color: secondaryTextColor, mt: 0.3 }}>
                Modify global policies, identifiers, and operational preferences for {activeItem}.
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<SaveRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
              sx={{
                backgroundColor: isDark ? "#6366F1" : "#4F46E5",
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: "0.82rem",
                borderRadius: "10px",
                px: 2.5,
                textTransform: "none",
                "&:hover": { backgroundColor: isDark ? "#4F46E5" : "#4338CA" },
              }}
            >
              Save Changes
            </Button>
          </Box>

          {/* Dynamic Configuration Form */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: 2.5,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: primaryTextColor, mb: 0.8 }}>
                Primary Practice / Entity Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                defaultValue="NeuroClinical Specialty Center"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: inputBg,
                    fontSize: "0.85rem",
                    color: primaryTextColor,
                    "& fieldset": { borderColor: cardBorder },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: primaryTextColor, mb: 0.8 }}>
                NPI / Organization Identifier
              </Typography>
              <TextField
                fullWidth
                size="small"
                defaultValue="NPI-9948102319"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: inputBg,
                    fontSize: "0.85rem",
                    color: primaryTextColor,
                    "& fieldset": { borderColor: cardBorder },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: primaryTextColor, mb: 0.8 }}>
                Default Clinical Timezone
              </Typography>
              <TextField
                fullWidth
                size="small"
                defaultValue="PKT (UTC+05:00)"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: inputBg,
                    fontSize: "0.85rem",
                    color: primaryTextColor,
                    "& fieldset": { borderColor: cardBorder },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: primaryTextColor, mb: 0.8 }}>
                Electronic Billing Tax ID (TIN)
              </Typography>
              <TextField
                fullWidth
                size="small"
                defaultValue="XX-XXX8921"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: inputBg,
                    fontSize: "0.85rem",
                    color: primaryTextColor,
                    "& fieldset": { borderColor: cardBorder },
                  },
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3.5, borderColor: cardBorder }} />

          <Typography sx={{ fontSize: "0.92rem", fontWeight: 800, color: primaryTextColor, mb: 1.5 }}>
            Automated Feature Toggles
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <FormControlLabel
              control={<Switch defaultChecked sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#4F46E5" } }} />}
              label={
                <Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: primaryTextColor }}>
                  Auto-synchronize ICD-10 Diagnosis codes with Clearinghouse
                </Typography>
              }
            />
            <FormControlLabel
              control={<Switch defaultChecked sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#4F46E5" } }} />}
              label={
                <Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: primaryTextColor }}>
                  Enable automatic ERA (Electronic Remittance Advice) claim postings
                </Typography>
              }
            />
            <FormControlLabel
              control={<Switch sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#4F46E5" } }} />}
              label={
                <Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: primaryTextColor }}>
                  Enforce Multi-Factor Authentication (MFA) for clinical staff
                </Typography>
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Settings;